// Tick-box lists for โรคประจำตัว / การแพ้ / ยาที่ใช้ประจำ on the patient form. Ticks are saved
// as comma-joined text in the existing patient fields (congenitalDisease / allergic /
// medicationHistory), so every screen and API that reads those fields keeps working. The
// form rebuilds the ticks from that text: matching labels tick, anything else goes to อื่นๆ.
// A label must never contain a comma.

export const MAX_LEN = 500;
export const NONE = "ไม่มี";

// Items with `detail` show a small input when ticked; it's saved after the label.
export const DISEASE_ITEMS = [
  { label: "สุขภาพดี" },
  { label: "ตั้งครรภ์", detail: "อายุครรภ์ เช่น 12 สัปดาห์" },
  { label: "ให้นมบุตร" },
  { label: "โรคเบาหวาน" },
  { label: "โรคหัวใจ" },
  { label: "ลิ้นหัวใจเทียม / เคยเป็นเยื่อบุหัวใจอักเสบ" },
  { label: "โรคหอบหืด" },
  { label: "โรคลมบ้าหมู" },
  { label: "โรคความดันโลหิตสูง" },
  { label: "เลือดออกผิดปกติ" },
  { label: "โรคตับอักเสบ" },
  { label: "โรคภูมิคุ้มกันบกพร่อง" },
  { label: "โรคไต / ฟอกไต" },
  { label: "โรคไทรอยด์" },
  { label: "ข้อเทียม" },
  { label: "มะเร็ง / ฉายรังสีศีรษะ-คอ / เคมีบำบัด" },
  { label: "โรคกระดูกพรุน" },
  { label: "โรคหลอดเลือดสมอง" },
];

export const ALLERGY_ITEMS = [
  { label: "Penicillin" },
  { label: "Sulfa" },
  { label: "Tetracycline" },
  { label: "Epinephrine or Adrenalin" },
  { label: "Base Metal Alloys Containing Nickel" },
  { label: "Acrylics or Denture Reline Materials" },
  { label: "น้ำยาง / Latex" },
  { label: "ยาชาเฉพาะที่ (Lidocaine / Articaine / Mepivacaine)" },
  { label: "NSAIDs / Aspirin" },
  { label: "Cephalosporins" },
  { label: "Chlorhexidine" },
  { label: "Iodine / Povidone" },
];

const group = (label, items) => ({ label, items: items.map((l) => ({ label: l })) });

export const MEDICATION_GROUPS = [
  group("ยาต้านการแข็งตัวของเลือด", [
    "Aspirin (แอสไพริน)",
    "Warfarin (วาร์ฟาริน)",
    "Clopidogrel (โคลพิโดเกรล)",
    "Heparin (เฮปาริน)",
    "Rivaroxaban (ริวารอกซาบัน)",
  ]),
  group("ยาเบาหวาน", [
    "Metformin (เมตฟอร์มิน)",
    "Insulin (อินซูลิน)",
    "Glipizide (กลิพิไซด์)",
    "Gliclazide (กลิคลาไซด์)",
  ]),
  group("ยาความดันโลหิต", [
    "Amlodipine (แอมโลดิพีน)",
    "Atenolol (อะทีโนลอล)",
    "Losartan (โลซาร์แทน)",
    "Enalapril (เอนาลาพริล)",
    "Nifedipine (นิเฟดิพีน)",
  ]),
  group("ยาต้านการสูญเสียมวลกระดูก", [
    "Bisphosphonates (บิสฟอสโฟเนต)",
    "Alendronate (อะเลนโดรเนต)",
    "Zoledronic acid (โซเลดรอนิก แอซิด)",
  ]),
  group("ยาแก้ปวด/ลดอักเสบ", [
    "Ibuprofen (ไอบูโพรเฟน)",
    "Paracetamol (พาราเซตามอล)",
    "Diclofenac (ไดโคลฟีแนค)",
    "Naproxen (นาพรอกเซน)",
  ]),
  group("ยาปฏิชีวนะ", [
    "Amoxicillin (อะม็อกซีซิลลิน)",
    "Clindamycin (คลินดามัยซิน)",
    "Metronidazole (เมโทรนิดาโซล)",
    "Azithromycin (อะซิโทรมัยซิน)",
  ]),
  group("ยาสเตียรอยด์", ["Prednisolone (เพรดนิโซโลน)", "Dexamethasone (เด็กซาเมทาโซน)"]),
  group("ยาอื่นๆ", [
    "Levothyroxine (ลีโวไทร็อกซีน)",
    "Simvastatin (ซิมวาสแทติน)",
    "Omeprazole (โอเมพราโซล)",
  ]),
];

// What staff have typed to mean "none" in the old free-text fields.
const NONE_WORDS = ["ไม่มี", "-", "ไม่แพ้", "ไม่แพ้ยา", "ปฏิเสธ", "none", "no", "nil", "nkda", "n/a"];

const norm = (s) => String(s).toLowerCase().replace(/\s+/g, " ").trim();
const clean = (s) => String(s || "").replace(/,/g, " ").replace(/\s+/g, " ").trim();

// "Aspirin (แอสไพริน)" also matches a bare "Aspirin".
const namesOf = (label) => {
  const bare = label.split(" (")[0];
  return bare === label ? [norm(label)] : [norm(label), norm(bare)];
};

/** @returns {{ has: true|false|null, ticked: Object<string,string>, other: string }} */
export function decodeChecklist(text, items) {
  const raw = String(text == null ? "" : text).trim();
  if (!raw) return { has: null, ticked: {}, other: "" };
  if (NONE_WORDS.includes(norm(raw))) return { has: false, ticked: {}, other: "" };

  const found = {};
  const rest = [];
  for (const token of raw.split(",").map((t) => t.trim()).filter(Boolean)) {
    const t = norm(token);
    const exact = items.find((i) => namesOf(i.label).includes(t));
    const withDetail = !exact && items.find((i) => i.detail && t.startsWith(`${norm(i.label)} `));
    if (exact) found[exact.label] = "";
    else if (withDetail) found[withDetail.label] = token.trim().slice(withDetail.label.length).trim();
    else rest.push(token);
  }
  const ticked = {};
  for (const i of items) if (i.label in found) ticked[i.label] = found[i.label];
  return { has: true, ticked, other: rest.join(", ") };
}

/** @returns {string|null} null when unanswered, or มี with nothing filled in */
export function encodeChecklist(state, items) {
  if (state.has === false) return NONE;
  if (state.has !== true) return null;
  const parts = items
    .filter((i) => i.label in state.ticked)
    .map((i) => [i.label, clean(state.ticked[i.label])].filter(Boolean).join(" "));
  const other = String(state.other || "").trim();
  if (other) parts.push(other);
  return parts.length ? parts.join(", ") : null;
}

/**
 * The tick-box panel's search: keeps items whose label contains the query, and a whole group
 * when its own name matches. Groups left with nothing are dropped.
 * @returns {{ label: string|null, items: object[] }[]}
 */
export function filterGroups(groups, query) {
  const q = norm(query || "");
  if (!q) return groups;
  return groups
    .map((g) => (g.label && norm(g.label).includes(q) ? g : { ...g, items: g.items.filter((i) => norm(i.label).includes(q)) }))
    .filter((g) => g.items.length);
}
